import type { CSSProperties, ReactNode } from 'react';
import type { ModalProps } from 'react-bootstrap';
import { Modal } from 'react-bootstrap';

/**
 * Where a modal sits on the layering scale. See `Docs/Layering.md`.
 *
 * 1 - a first-level modal, opened from the page. The default.
 * 2 - a modal opened from a modal: a confirm, a delete prompt, a detail dialog.
 * 3 - a third level. Rare; add a comment naming the two modals below it.
 */
export type ModalStackLevel = 1 | 2 | 3;

type CSSPropertiesWithVariables = CSSProperties & {
  [property: `--${string}`]: string | number | undefined;
};

// Callers may pass plain CSSProperties (often cast, to carry --bs-* variables) or the
// variable-aware shape; zIndex is dropped either way - the level decides it.
type BaseModalStyle = Omit<CSSPropertiesWithVariables, 'zIndex'>;

const STACK_LEVEL_ZINDEX: Record<ModalStackLevel, string> = {
  1: 'var(--z-modal)',
  2: 'var(--z-modal-2)',
  3: 'var(--z-modal-3)',
};

/**
 * Base modal wrapper used throughout the app.
 * @param props Modal options and modal content.
 * @returns Modal wrapper component.
 */
const BaseModal = ({
  show,
  handleShow,
  style,
  contentStyle,
  bodyClassName = '',
  closeButton = true,
  size = 'xl',
  title,
  footer,
  backgroundColor,
  borderColor,
  borderWidth,
  scrollable = false,
  children,
  stackLevel = 1,
  backdropClassName,
  container,
  ...props
}: Omit<ModalProps, 'show' | 'onHide' | 'size' | 'style' | 'container' | 'children'> & {
  show: boolean;
  handleShow?: ModalProps['onHide'];
  style?: BaseModalStyle | Omit<CSSProperties, 'zIndex'>;
  contentStyle?: CSSProperties;
  bodyClassName?: string;
  closeButton?: boolean;
  size?: NonNullable<ModalProps['size']> | 'md';
  title?: ReactNode;
  footer?: ReactNode;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string | number;
  scrollable?: boolean;
  children?: ReactNode;
  stackLevel?: ModalStackLevel;
  container?: ModalProps['container'];
}) => {
  const resolvedSize: ModalProps['size'] = size === 'md' ? undefined : size;
  const resolvedLevel: ModalStackLevel = STACK_LEVEL_ZINDEX[stackLevel] ? stackLevel : 1;
  const callerStyle: CSSPropertiesWithVariables = { ...style };
  delete callerStyle.zIndex;
  const resolvedStyle: CSSPropertiesWithVariables = {
    ...callerStyle,
    '--bs-modal-bg': backgroundColor ?? 'var(--color-primary-shadow)',
    '--bs-modal-border-width': borderWidth,
    '--bs-modal-border-color': borderColor,
    '--bs-modal-zindex': STACK_LEVEL_ZINDEX[resolvedLevel],
  };

  // The modal carries its own z-index inline, but react-bootstrap renders the backdrop as a
  // sibling with no per-instance style hook, so the level travels to it as a class. Keeping this
  // declarative is what lets two modals at different levels hold their own backdrops - see
  // `.modal-backdrop.modal-stack-*` in global.scss.
  const resolvedBackdropClassName = [`modal-stack-${resolvedLevel}`, backdropClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal
      size={resolvedSize}
      show={show}
      onHide={handleShow}
      container={container}
      scrollable={scrollable}
      backdropClassName={resolvedBackdropClassName}
      style={resolvedStyle}
      {...props}
    >
      {(closeButton || title) && (
        <Modal.Header closeButton={closeButton}>
          <Modal.Title className="fs-4 fw-bold w-100">{title}</Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body className={`d-flex flex-column ${bodyClassName}`} style={contentStyle}>
        {children}
      </Modal.Body>
      {footer && <Modal.Footer>{footer}</Modal.Footer>}
    </Modal>
  );
};

export default BaseModal;
